<template>
  <div id="surprise-me" class="scroll-dest">
    <div
      class="grey-background content-container category-grid surprise-section"
      :class="tags && tags.length ? 'surprise-me-top-pad' : ''"
    >
      <div class="surprise-me">
        <div v-show="error || primaryVideo" class="surprise-me-results">
          <!-- primary result -->
          <div class="surprise-me-main">
            <callout v-if="error" type="error">
              <div>{{ error }}</div>
            </callout>
            <video-card v-if="primaryVideo" :video="primaryVideo" custom-expander-key="surprise" />
          </div>
          <!-- secondary result, if not enough, show placeholder slots -->
          <div v-if="secondaryVideos.length" class="surprise-me-related">
            <div
              v-for="video in secondaryVideos"
              :key="video['id']"
              class="surprise-me-related-result -pointer"
            >
              <video-mini-card :video="video" @click-action="swapPrimaryResult" />
            </div>
            <div
              v-for="placeHolder in placeholderItems"
              :key="placeHolder"
              class="surprise-me-related-result"
            >
              <!-- placeholder items that mini content-items -->
              <div class="surprise-me-placeholder">
                <span class="eyebrow demi caps">Limited Results<br />Available</span>
              </div>
            </div>
          </div>
        </div>

        <!-- forward, back, and submit CTAs -->
        <div class="surprise-me-submit">
          <div class="prev-surprises">
            <icon-button
              v-show="history.currentIndex > 0"
              classes="-small -anchor-link"
              icon="fast_rewind"
              @click-action="goBack"
            />
          </div>
          <submit-button
            classes="-main"
            :submitting="loading"
            label="Surprise Me"
            submitting-label="processing..."
            @submit-action="handleSubmit"
          />
          <div class="next-surprises">
            <icon-button
              v-show="history.currentIndex < history.previousResults.length - 1"
              classes="-small -anchor-link"
              icon="fast_forward"
              @click-action="goForward"
            />
          </div>
        </div>
      </div>

      <div class="surprise-me-close">
        <button
          v-if="error || primaryVideo"
          class="btn -remove"
          aria-label="Collapse Surprise Me"
          @click.prevent="reset"
        >
          <i class="material-symbols-outlined">close</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import SubmitButton from '../buttons/submit-button.vue'
import VideoCard from './video-card.vue'
import Callout from '../notifications/callout.vue'
import VideoMiniCard from './video-mini-card.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import { trackEvent } from '@/utils/analytics'

/**
 * Custom expander that display a random search result based on the selected tags
 */
export default defineComponent({
  name: 'VideoSurpriseMe',
  components: { IconButton, VideoMiniCard, SubmitButton, VideoCard, Callout },
  props: {
    favoritesOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: '',

      // current result
      primaryVideo: null as Record<string, any> | null,
      secondaryVideos: [] as any[],
      total: 0,

      // history for forward / back support
      // could be added to pinia for future if used beyond video
      history: {
        currentIndex: 0, // pointer to where we are in previousResults
        previousResults: [], // { primary: id, secondary => [id, id, id] }
        resultMap: {}, // id => { props },
      } as Record<string, any>,
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['cdn']),
    ...mapState(useSearchStore, [
      'apiParameters',
      // array of tag items, used to toggle padding class
      'tags',
      'clearSurpriseMe',
    ]),

    // TODO: this is used a lot, should clean up usage
    imageUrlPrefix() {
      // get siteassets url from page load, allows CDN url to be added
      const siteAssetsPrefix = this.cdn.siteAssetsPrefix || '/siteassets'
      return siteAssetsPrefix + '/video'
    },

    // if there are are not 3 mini cards, show place holders instead
    placeholderItems() {
      const placeholders = []
      const secondaryCount = this.secondaryVideos.length
      const placeholderCount = 3

      // add to placeholder array to display blank slots
      for (let i = secondaryCount; i < placeholderCount; i += 1) {
        placeholders.push(i)
      }

      return placeholders
    },
  },

  // force close if pinia value changes (see video-page fetchVideos method)
  watch: {
    clearSurpriseMe(newValue: boolean, oldValue: boolean) {
      if (newValue && !oldValue) {
        this.reset()
      }
    },
  },

  mounted() {
    this.reset()
  },

  methods: {
    ...mapActions(useSearchStore, ['setClearSurpriseMe']),

    // close the surprise me form
    reset() {
      this.error = ''
      this.loading = false
      this.primaryVideo = null
      this.secondaryVideos = []
      this.total = 0
      this.history = {
        currentIndex: 0,
        previousResults: [], // { primary: number, secondary: [number, number, number])
        resultMap: {},
      }

      this.setClearSurpriseMe(false)
    },

    // go back one in history
    goBack() {
      // do nothing if loading
      if (this.loading) {
        return
      }
      // go back in history one result
      const newIndex = this.history.currentIndex - 1
      const backOne = this.history.previousResults[newIndex] as any

      // change index and replace current results
      this.history.currentIndex = newIndex
      this.setCurrentResult(backOne.primary, backOne.secondary)
    },

    goForward() {
      // do nothing if loading
      if (this.loading) {
        return
      }

      // go back in history one result
      const newIndex = this.history.currentIndex + 1
      const forwardOne = this.history.previousResults[newIndex] as any

      // change index and replace current results
      this.history.currentIndex = newIndex
      this.setCurrentResult(forwardOne.primary, forwardOne.secondary)
    },

    // calculate video ids to exclude so that the same results are not repeated
    getExcludeIds(): number[] {
      if (
        !this.history ||
        !this.history.previousResults ||
        !Array.isArray(this.history.previousResults)
      ) {
        return []
      }

      // restrict potential excluded IDs by current index
      // reverse array first so that most recently viewed is excluded  (before limit)
      const pastHistory = this.history.previousResults
        .filter((prev: any, i: number) => i <= this.history.currentIndex)
        .map((prev: any) => prev.primary)
        .reverse()

      // max limit for excluded IDs (repeated results)
      let limit = 5

      // using floor so if only one 1 result, nothing will be excluded
      let halfTotal = Math.floor(this.total / 2)

      // limit exclude (repeats) to 5 OR half the list
      limit = halfTotal < limit ? halfTotal : limit
      // console.log(this.history.previousResults, pastHistory, this.total, limit, pastHistory.slice(0, limit));

      return pastHistory.slice(0, limit)
    },

    // uses current secondary from history
    swapPrimaryResult(video: any) {
      // set from history
      let secondary = this.secondaryVideos.map((video) => video.id)

      // remove id from secondary and replace with primary
      secondary = secondary.map((id) => {
        if (id === video.id && this.primaryVideo) {
          return this.primaryVideo.id
        }

        return id
      })

      this.setCurrentResult(video.id, secondary)
    },

    // assumes history.resultMap is already set
    setCurrentResult(primary: number, secondary: number[]) {
      // main card
      this.primaryVideo = {
        ...((this.history.resultMap && this.history.resultMap[primary]) || {}),
      }

      // secondary cards
      this.secondaryVideos = secondary.map(
        (id) => this.history.resultMap && this.history.resultMap[id],
      )
    },

    pushHistory(videos: Array<any>) {
      // get primary and secondary items
      if (!Array.isArray(videos)) {
        return
      }

      const formattedResults = videos.reduce(
        (acc, video, index) => {
          // add any result to map
          acc.map[video.id] = video

          // save first result id into primary
          if (index === 0) {
            acc.primary = video.id
            return acc
          }

          // all other results go into secondary
          acc.secondary.push(video.id)
          return acc
        },
        { primary: null, secondary: [], map: {} },
      )

      // update history map
      this.history.resultMap = {
        ...this.history.resultMap,
        ...formattedResults.map,
      }

      // if user has clicked back, index is less than history
      if (
        this.history.previousResults.length &&
        this.history.currentIndex < this.history.previousResults.length - 1
      ) {
        // index not at the end, clear history in front of index
        this.history.previousResults.splice(this.history.currentIndex + 1)
      }

      // index is at the end, push to history results
      this.history.previousResults.push({
        primary: formattedResults.primary,
        secondary: formattedResults.secondary,
      })

      // bump index
      this.history.currentIndex = this.history.previousResults.length - 1

      // return back the result formatted to be used as current result
      return formattedResults
    },

    // form submit
    async handleSubmit() {
      this.loading = true
      this.error = ''

      // analytics event
      const eventAction = this.favoritesOnly ? 'Favorites CTA Click' : 'Surprise Me CTA Click'
      trackEvent({
        category: 'Video Surprise Me',
        action: eventAction,
      })

      try {
        const response = await fbApi.get('/videos/surprise-me', {
          searchParams: objToSearchParams({
            // use all selected filters
            ...this.apiParameters,

            // needed to tell elastic search to sort randomly
            // and omit previous results
            surprise: 1,
            excludes: this.getExcludeIds(),

            // restrict to favorites
            favorites: this.favoritesOnly ? 1 : 0,

            // list of excluded videos ids from previous results

            // safari seems to cache when used with back button :(
            // I tried all sorts of cache control + vary headers and it still
            // returned from memory if clicked AFTER the back button ws used
            bustCache: Date.now(),
          }),
        })

        this.loading = false
        const { body } = response

        // process results
        if (
          body &&
          body.data &&
          body.data.paginatedVideos &&
          Array.isArray(body.data.paginatedVideos.data) &&
          body.data.paginatedVideos.data.length
        ) {
          // track initial total as it is used with excluded id calculation
          // only update if total is not already set
          // Note: if user clicks any filters, total will reset to 0
          this.total = this.total || body.data.paginatedVideos.total

          // push history and set first result
          const formattedResults = this.pushHistory(body.data.paginatedVideos.data)
          if (formattedResults) {
            this.setCurrentResult(formattedResults.primary, formattedResults.secondary)
          }
        } else {
          //clear active results
          this.primaryVideo = null
          this.secondaryVideos = []

          this.error = 'No results, please expand search criteria.'
        }
      } catch (e) {
        // API failed
        this.loading = false
        this.error = 'Oops... something went wrong.  Please try again.'
        this.primaryVideo = null
      }
    },
  },
})
</script>
